import {Injectable} from '@angular/core';
import * as Sentry from "@sentry/angular";
import {environment} from 'src/environments/environment';
import {AppVersionService} from './app-version.service';
import {AuthorizationServiceProvider} from "../auth_profile/services/authorization-service.provider";
import {filter, repeat, switchMap, take, zip} from "rxjs";
import {ThemeService} from "./theme.service";

@Injectable({
  providedIn: 'root'
})
export class SentryService {
  private static restoreDocumentScroll() {
    const body = document?.body;
    const html = document?.documentElement;
    if (body) {
      body.style.removeProperty("overflow");
      body.style.removeProperty("overflow-y");
      body.style.removeProperty("padding-right");
      body.style.removeProperty("touch-action");
      body.style.removeProperty("position");
      body.style.removeProperty("width");
      body.classList.remove("modal-open");
    }
    if (html) {
      html.style.removeProperty("overflow");
      html.style.removeProperty("overflow-y");
      html.style.removeProperty("touch-action");
    }

    if (!document?.querySelector(".offcanvas.show")) {
      document
        ?.querySelectorAll(".offcanvas-backdrop")
        .forEach(backdrop => backdrop.remove());
    }
    if (!document?.querySelector(".modal.show")) {
      document
        ?.querySelectorAll(".modal-backdrop")
        .forEach(backdrop => backdrop.remove());
    }
  }

  private static scheduleScrollRestore() {
    [0, 100, 300].forEach(delay =>
      setTimeout(() => SentryService.restoreDocumentScroll(), delay)
    );
  }

  constructor(appVersion: AppVersionService,
              themeService: ThemeService,
              authProvider: AuthorizationServiceProvider) {
    if(environment.logToSentry)
    {
      authProvider.getAuthDetailsService().pipe(
        switchMap(api =>
            zip(api.getUserEmail(), api.getSubject(), appVersion.getVersion())
        ),
        repeat({delay: 100}),
        filter(([email, subject, _]) => !!email && !!subject),
        take(1)
      ).subscribe(([email, subject, version]) =>
        Sentry.getCurrentScope().addEventProcessor(event => {
          Sentry.setTag("userEmail", email)
          Sentry.setTag("subject", subject)
          return new Promise(resolve =>
            resolve({
              ...event,
              release: version
            })
          )
        })
      )

      themeService.getTheme().subscribe((theme: "dark" | "light") => {
        const feedback = Sentry.getFeedback();
        feedback.remove()
        Sentry.feedbackAsyncIntegration({
          colorScheme: theme
        })
        let newFeedback = Sentry.getFeedback()
        const buttons = document.querySelectorAll("#reportABugButton")
        if (buttons.length) {
          buttons.forEach(button => {
            newFeedback.attachTo(button, {
              colorScheme: theme
            })
            button.setAttribute("data-feedback-attached", "true")
          })
        }
      })
    }
   }

  reportError(e: Error) {
    Sentry.captureException(e);
  }

  static async initSentry() {
    if(environment.logToSentry)
      Sentry.init({
        dsn: environment.sentryDsn,
        sampleRate: 0.3,
        integrations: [
          // Registers and configures the Tracing integration,
          // which automatically instruments your application to monitor its
          // performance, including custom Angular routing instrumentation
          Sentry.browserTracingIntegration(),
          // Registers the Replay integration,
          // which automatically captures Session Replays
          Sentry.replayIntegration(),
          Sentry.feedbackIntegration({
            autoInject: false,
            onFormClose: () => SentryService.scheduleScrollRestore(),
            onFormSubmitted: () => SentryService.scheduleScrollRestore(),
            onSubmitError: () => SentryService.scheduleScrollRestore()
          }),
        ],
        tracePropagationTargets: ["localhost", environment.sentryTarget],
        environment: environment.envName,

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 0.1,
      });
  }
}
